import React from 'react';
import { AlertCircle } from 'lucide-react';
import { useThemeMode } from '../../theme/ThemeProvider';

interface FloatingInputProps {
    label: string;
    value: any;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    error?: string;
    type?: string;
    required?: boolean;
    id?: string;
    readOnly?: boolean;
}

const FloatingInput: React.FC<FloatingInputProps> = ({
    label,
    value,
    onChange,
    error,
    type = 'text',
    required = false,
    id,
    readOnly = false,
}) => {
    const { isDark } = useThemeMode();

    return (
        <div className="w-full">
            <label htmlFor={id} className={`block mb-1.5 text-nav-xs font-semibold tracking-wide text-slate-600 dark:text-slate-300`}>
                {label}
                {required && <span className="text-red-500 ml-0.5">*</span>}
            </label>
            <input
                id={id}
                type={type}
                value={value}
                onChange={onChange}
                required={required}
                readOnly={readOnly}
                className={[
                    'w-full px-3.5 py-2.5 text-nav-sm rounded-xl border outline-none',
                    'transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]',
                    readOnly ? 'cursor-default select-none opacity-70' : '',
                    isDark
                        ? 'bg-[#1C2E4C] text-slate-100 placeholder:text-slate-400'
                        : 'bg-white text-slate-800 placeholder:text-slate-400',
                    error
                        ? isDark
                            ? 'border-red-400/50 focus:border-red-400 focus:shadow-[0_0_0_3px_rgba(239,68,68,0.12)]'
                            : 'border-red-300 focus:border-red-400 focus:shadow-[0_0_0_3px_rgba(239,68,68,0.10)]'
                        : readOnly
                            ? isDark ? 'border-white/10' : 'border-slate-200'
                            : isDark
                                ? 'border-white/[0.18] hover:border-white/30 focus:border-brand-400 focus:shadow-[0_0_0_3px_rgba(43,141,224,0.15)]'
                                : 'border-slate-300 hover:border-brand-400/50 focus:border-brand-500 focus:shadow-[0_0_0_3px_rgba(3,93,184,0.10)]',
                ].join(' ')}
            />
            {error && (
                <p className={`mt-1.5 flex items-center gap-1.5 text-nav-xs text-red-600 dark:text-red-400`}>
                    <AlertCircle size={13} />
                    {error}
                </p>
            )}
        </div>
    );
};

export default FloatingInput;
